import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import i18n from 'i18next';
import { CircleHelpIcon } from 'lucide-react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import ToolkitWrapper from '@/components/organisms/Toolkit/ToolkitWrapper';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { setVariable } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { SetVariablesAction } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { SET_VARIABLES_DOCS_URL } from '@/util/constants';
import { capitalizeFirstLetter, removeDollarSign } from '@/util/util';
import { LENGTH_L, LIVE_INSTRUCTIONS_LIMIT } from '@/util/validation/constants';
import { saveAsRefRegExp } from '@/util/validation/patterns';

import SetVariablesInput from './SetVariablesInput/SetVariablesInput';

const RESERVED_VARIABLES = ['tags', 'user', 'global'];

// Create schema inside component and infer type
const createSetVariableSchema = (reservedVariables: string[]) =>
  yup.object().shape({
    variable: yup
      .string()
      .matches(saveAsRefRegExp, { message: () => i18n.t('validation.pattern') })
      .max(LENGTH_L)
      .notOneOf(reservedVariables, ({ value }) =>
        i18n.t('dialog.set_variables.reserved_variables', { 0: value })
      )
      .required(),
    value: yup.string().when('variable', (variable, schema) => {
      // Increase the max length for live_instructions
      if (variable[0] === 'live_instructions') {
        return schema.min(1).max(LIVE_INSTRUCTIONS_LIMIT).required();
      }
      return schema.min(1).max(LENGTH_L).required();
    }),
    selectedVariable: yup.string(),
  });

export type SetVariablesActionForm = yup.InferType<ReturnType<typeof createSetVariableSchema>>;

const ToolkitSetVariables = () => {
  // Redux selectors
  const brainId = useSelector(selectBrainId);
  const { actionId, variable, value } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as SetVariablesAction;
    // For now, we only support one variable per action
    // In the future, we can send multiple variables, because the backend supports it
    const firstVariable = selectedAction?.variables
      ? selectedAction.variables[0]
      : { value: '', variable: '' };
    return {
      actionId: selectedAction?.action_id,
      variable: firstVariable?.variable,
      value: firstVariable?.value,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  // Custom hooks
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getContextVariables } = useDialogs(brainId);
  const contextVariables = getContextVariables();

  // Local variables
  const categorizedComboboxOptions = useMemo(
    () =>
      contextVariables
        .map((variable) => ({
          value: removeDollarSign(variable.value),
          label: variable.label,
          category: t('dialog.set_variables.auto_complete_group_by'),
        }))
        // Filter out the tags variable
        .filter((option) => option.value !== 'tags'),
    [contextVariables, t]
  );

  const defaultVariable = useMemo(() => {
    // Return the raw variable value if it exists
    return variable || '';
  }, [variable]);

  // RHF
  const {
    trigger,
    setValue,
    getValues,
    control,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<SetVariablesActionForm>({
    mode: 'onChange',
    defaultValues: { variable: defaultVariable, value: value ?? '' },
    resolver: yupResolver(
      createSetVariableSchema(RESERVED_VARIABLES)
    ) as Resolver<SetVariablesActionForm>,
  });

  const variableErrorMessage = capitalizeFirstLetter(errors.variable?.message || '');
  const valueErrorMessage = capitalizeFirstLetter(errors.value?.message || '');

  // Saves the new variable and its value to the redux store
  const saveNewVariable = useCallback(
    (data) => {
      dispatch(setVariable({ actionId, variables: [data] }));
    },
    [actionId, dispatch]
  );

  const saveAllVariablesInRedux = useCallback(() => {
    const variableValue = removeDollarSign(getValues('variable'));
    const valueData = getValues('value');

    saveNewVariable({ variable: variableValue, value: valueData });
  }, [getValues, saveNewVariable]);

  /**  Triggers:
   * 1. When the user types input
   * 2. When the user selects an option from the dropdown menu
   */
  const handleVariableChange = useCallback(
    async (selectedValue: string) => {
      setValue('variable', selectedValue);

      // We need to trigger the validation because the schema changes
      // when the variable is the Live instructions
      await trigger();

      saveAllVariablesInRedux();
    },
    [saveAllVariablesInRedux, setValue, trigger]
  );

  // Handle creating a new variable
  const handleCreateNewVariable = useCallback(
    async (newVariableName: string) => {
      const cleanedVariable = removeDollarSign(newVariableName);
      setValue('variable', cleanedVariable);
      await trigger();
      saveAllVariablesInRedux();
    },
    [setValue, trigger, saveAllVariablesInRedux]
  );

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.set_variables'),
    type: 'set_variables',
  });

  useEffect(() => {
    (async () => {
      await trigger();
    })();
  }, [trigger]);

  useEffect(() => {
    if (isDirty && dirtyFields.variable) {
      setFieldError('variable', variableErrorMessage);
    }
  }, [variableErrorMessage, setFieldError, isDirty, dirtyFields.variable]);

  useEffect(() => {
    if (isDirty && dirtyFields.value) {
      setFieldError('value', valueErrorMessage);
    }
  }, [valueErrorMessage, setFieldError, isDirty, dirtyFields.value]);

  return (
    <ToolkitWrapper type="set_variables">
      <FormAutocomplete
        control={control}
        name="variable"
        label={
          <label className="mb-1 flex items-center gap-1 text-sm leading-5 font-normal text-foreground">
            {t('dialog.set_variables.variable_name.label')}
            <InfoTooltip
              content={t('dialog.set_variables.variable_name.tooltip')}
              learnMoreUrl={SET_VARIABLES_DOCS_URL}
              side="right"
            >
              <CircleHelpIcon size={12} className="text-foreground-muted" />
            </InfoTooltip>
          </label>
        }
        placeholder={t('dialog.set_variables.variable_name.placeholder')}
        options={categorizedComboboxOptions}
        onChange={handleVariableChange}
        onCreateNew={handleCreateNewVariable}
        error={!!errors.variable}
        errorMessage={variableErrorMessage}
      />

      <SetVariablesInput
        setValue={setValue}
        control={control}
        error={errors.value}
        onSave={saveAllVariablesInRedux}
      />
    </ToolkitWrapper>
  );
};

export default ToolkitSetVariables;
